import { memo } from "react"
import { Link } from "react-router-dom"
import { EXCLUDED_TICKERS } from "~shared/cashtag"
import { CashtagPreview } from "./CashtagPreview"
import { ResolvedTokenAddress } from "./ResolvedTokenAddress"

const URL_REGEX =
	/(?:https?:\/\/|www\.)[^\s<>'")\]]+|(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:com|org|net|io|co|app|dev|xyz|fun|gg|me|tv|info|art|lol)(?:\/[^\s<>'")\]]*)?/gi

const BASE58_CHARS = /^[1-9A-HJ-NP-Za-km-z]+$/

const COMBINED_REGEX = new RegExp(
	`(${URL_REGEX.source})|(@[a-z0-9_]{3,20}\\b)|([1-9A-Za-z]{43,44})|(\\$[A-Za-z]{2,10}\\b)`,
	"gi",
)

interface LinkifiedTextProps {
	className?: string
	onCashtagClick?: (ticker: string) => void
	onTokenAddressClick?: (mint: string) => void
	text: string
}

export const LinkifiedText = memo(function LinkifiedText({
	className,
	onCashtagClick,
	onTokenAddressClick,
	text,
}: LinkifiedTextProps) {
	const nodes: React.ReactNode[] = []
	let lastIndex = 0
	let keyIndex = 0

	for (const match of text.matchAll(COMBINED_REGEX)) {
		const matchText = match[0]
		const matchStart = match.index ?? 0
		const isMention = match[2] !== undefined
		const isToken = match[3] !== undefined
		const isCashtag = match[4] !== undefined

		if (isMention && matchStart > 0 && !/\s/.test(text[matchStart - 1])) {
			continue
		}

		if (isToken) {
			if (!BASE58_CHARS.test(matchText)) {
				continue
			}
			if (matchStart > 0 && !/\s/.test(text[matchStart - 1])) {
				continue
			}
			const matchEnd = matchStart + matchText.length
			if (matchEnd < text.length && /[a-zA-Z0-9]/.test(text[matchEnd])) {
				continue
			}
		}

		if (isCashtag) {
			if (matchStart > 0 && !/\s/.test(text[matchStart - 1])) {
				continue
			}
			if (EXCLUDED_TICKERS.has(matchText.slice(1).toUpperCase())) {
				continue
			}
		}

		if (matchStart > lastIndex) {
			nodes.push(text.slice(lastIndex, matchStart))
		}

		if (isCashtag) {
			const ticker = matchText.slice(1).toUpperCase()
			nodes.push(
				<CashtagPreview key={`cashtag-${keyIndex++}`} onClick={onCashtagClick} ticker={ticker}>
					{matchText}
				</CashtagPreview>,
			)
		} else if (isMention) {
			const handle = matchText.slice(1).toLowerCase()
			nodes.push(
				<Link
					className="font-semibold text-app-mention hover:underline"
					key={`mention-${keyIndex++}`}
					to={`/user/${handle}`}
				>
					{matchText}
				</Link>,
			)
		} else if (isToken) {
			nodes.push(
				<ResolvedTokenAddress
					key={`token-${keyIndex++}`}
					mint={matchText}
					onClick={onTokenAddressClick}
				/>,
			)
		} else {
			const href = matchText.startsWith("http") ? matchText : `https://${matchText}`
			nodes.push(
				<a
					className="text-white underline"
					href={href}
					key={`link-${keyIndex++}`}
					rel="noopener noreferrer"
					target="_blank"
				>
					{matchText}
				</a>,
			)
		}

		lastIndex = matchStart + matchText.length
	}

	if (lastIndex < text.length) {
		nodes.push(text.slice(lastIndex))
	}

	return <span className={className}>{nodes.length > 0 ? nodes : text}</span>
})
